/**
 * BlogArticlesEmbed — embeddable blog articles grid rendered inside a CMS page.
 *
 * Used when the {{blog-articles}} shortcode or
 * <div data-gsgo-embed="blog-articles"></div> marker is placed in a CMS page body.
 * For featured-only: {{blog-articles-featured}} or data-gsgo-embed="blog-articles-featured".
 *
 * Features: loads published articles (6 per page), shows load-more button.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Heart, Eye, ChevronRight, ArrowRight } from 'lucide-react';
import {
  getPublishedArticles,
  getFeaturedPublishedArticles,
  estimateReadTime,
  formatDate,
  type BlogArticle,
  type QueryDocumentSnapshot,
} from '@/lib/blog';
import type { DocumentData } from 'firebase/firestore';

const PAGE_SIZE = 6;

export type BlogArticlesEmbedVariant = 'all' | 'featured';

interface BlogArticlesEmbedProps {
  /** 'all' = all published articles; 'featured' = only published articles marked featured. */
  variant?: BlogArticlesEmbedVariant;
}

export function BlogArticlesEmbed({ variant = 'all' }: BlogArticlesEmbedProps): JSX.Element {
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      const fetcher = variant === 'featured' ? getFeaturedPublishedArticles : getPublishedArticles;
      const { articles: data, lastDoc: last } = await fetcher(PAGE_SIZE);
      setArticles(data);
      setLastDoc(last);
      setHasMore(data.length === PAGE_SIZE);
    } catch (err) {
      console.error('[BlogArticlesEmbed] Failed to load articles:', err);
    } finally {
      setLoading(false);
    }
  }, [variant]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  async function loadMore() {
    if (!lastDoc || loadingMore) return;
    setLoadingMore(true);
    try {
      const fetcher = variant === 'featured' ? getFeaturedPublishedArticles : getPublishedArticles;
      const { articles: more, lastDoc: last } = await fetcher(PAGE_SIZE, lastDoc);
      setArticles((prev) => [...prev, ...more]);
      setLastDoc(last);
      setHasMore(more.length === PAGE_SIZE);
    } catch (err) {
      console.error('[BlogArticlesEmbed] Failed to load more articles:', err);
    } finally {
      setLoadingMore(false);
    }
  }

  /** Featured: constrain to content column (placeholder div is replaced and loses max-w-6xl). All: standalone section. */
  const isInline = variant === 'featured';
  const contentWrapperClass = isInline
    ? 'max-w-6xl mx-auto w-full min-w-0'
    : 'max-w-5xl mx-auto w-full px-4 sm:px-8';

  if (loading) {
    return (
      <div className={contentWrapperClass}>
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className={contentWrapperClass}>
        <div className="py-12 text-center">
          <p className="text-gray-500">
            {variant === 'featured'
              ? 'No featured articles yet. Check back soon.'
              : 'No articles published yet. Check back soon.'}
          </p>
        </div>
      </div>
    );
  }

  const gridAndFooter = (
    <>
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 ${isInline ? 'w-full min-w-0' : ''}`}>
        {articles.map((article) => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </div>
      <div className="mt-8 flex flex-col items-center gap-4">
        {!isInline && hasMore && (
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
          >
            {loadingMore ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-500 border-t-transparent" />
            ) : (
              <ChevronRight size={16} />
            )}
            {loadingMore ? 'Loading…' : 'Load more articles'}
          </button>
        )}
<Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
          >
            View all articles
            <ArrowRight size={14} />
          </Link>
      </div>
    </>
  );

  if (isInline) {
    return (
      <div className={contentWrapperClass} aria-label="Featured blog articles" role="region">
        {gridAndFooter}
      </div>
    );
  }

  return (
    <section className="py-8 sm:py-10" aria-label="Blog articles">
      <div className={contentWrapperClass}>
        {gridAndFooter}
      </div>
    </section>
  );
}

function ArticleCard({ article }: { article: BlogArticle }) {
  const readTime = estimateReadTime(article.body);
  const tags = article.tags?.slice(0, 2) ?? [];

  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
    >
      {/* Featured image */}
      {article.featuredImage ? (
        <div className="aspect-[16/9] overflow-hidden bg-gray-100">
          <img
            src={article.featuredImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      ) : (
        <div className="aspect-[16/9] bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <span className="text-4xl font-bold text-blue-200">
            {article.title.charAt(0)}
          </span>
        </div>
      )}

      <div className="flex flex-col flex-1 p-4">
        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h3 className="font-semibold text-gray-900 leading-snug group-hover:text-blue-600 transition-colors line-clamp-2">
          {article.title}
        </h3>

        {/* Excerpt */}
        {article.excerpt && (
          <p className="mt-1.5 text-sm text-gray-500 line-clamp-2 flex-1">{article.excerpt}</p>
        )}

        {/* Meta */}
        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-400">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Calendar size={11} />
              {formatDate(article.publishedAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock size={11} />
              {readTime}m read
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <Heart size={11} />
              {article.likeCount ?? 0}
            </span>
            {article.showViewCount && (
              <span className="inline-flex items-center gap-1">
                <Eye size={11} />
                {article.viewCount ?? 0}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
